import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../Css/Botones/botones.css';
import '../../Css/catalogobase/catalogobase.css';
import '../../Css/tables/tabla_consultar.css';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Password } from 'primereact/password';

import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';

import { 
    obtenerEntidades,
    eliminarEntidad,
    API_URLS,
    BASE_URL
} from '../../Services/servicioURL';

// Importar crypto-js para encriptación
import CryptoJS from 'crypto-js';

interface Licencia {
    id: number;
    tipo: string;
    clave: string;
    descripcion: string;
    cveemp: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    version: string;
    observaciones: string;
    empleado?: {
        cveemp: string;
        nomemp: string;
        sucursal: string;
    };
}

interface Empleado {
    cveemp: string;
    nomemp: string;
    sucursal: string;
    departamento: string;
}

interface Sucursal {
    id: number;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

const Licencias: React.FC = () => {
    const [mode, setMode] = useState<'view' | 'new' | 'edit'>('view');
    const [mostrarTabla, setMostrarTabla] = useState(false);
    const [mostrarEstadisticas, setMostrarEstadisticas] = useState(false);
    const [data, setData] = useState<Licencia[]>([]);
    const [empleados, setEmpleados] = useState<Empleado[]>([]);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroEstatus, setFiltroEstatus] = useState('');
    const [estadisticas, setEstadisticas] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // Campos del formulario
    const [id, setId] = useState<number | null>(null);
    const [tipo, setTipo] = useState('');
    const [clave, setClave] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [cveemp, setCveemp] = useState('');
    const [sucursal, setSucursal] = useState('');
    const [departamento, setDepartamento] = useState('');
    const [estatus, setEstatus] = useState('ACTIVA');
    const [version, setVersion] = useState('');
    const [observaciones, setObservaciones] = useState('');

    // Estado para mostrar datos encriptados/desencriptados
    const [mostrarEncriptado, setMostrarEncriptado] = useState(true);
    const [claveEncriptacion, setClaveEncriptacion] = useState('');

    const toast = useRef<Toast>(null);

    // Clave maestra para encriptación (en producción debería venir de variables de entorno)
    const MASTER_KEY = 'BabatsaLicencias2024!@#$%^&*()_+SecureKey';

    const tiposLicencia = [
        { label: 'Windows 10 Pro', value: 'Windows 10 Pro' },
        { label: 'Windows 11 Pro', value: 'Windows 11 Pro' },
        { label: 'Microsoft Office 365', value: 'Microsoft Office 365' },
        { label: 'Microsoft Office 2021', value: 'Microsoft Office 2021' },
        { label: 'Antivirus Kaspersky', value: 'Antivirus Kaspersky' },
        { label: 'Antivirus Norton', value: 'Antivirus Norton' },
        { label: 'Adobe Acrobat Pro', value: 'Adobe Acrobat Pro' },
        { label: 'AutoCAD', value: 'AutoCAD' },
        { label: 'Otro', value: 'Otro' }
    ];

    const estatusOptions = [
        { label: 'ACTIVA', value: 'ACTIVA' },
        { label: 'INACTIVA', value: 'INACTIVA' },
        { label: 'VENCIDA', value: 'VENCIDA' },
        { label: 'REVOCADA', value: 'REVOCADA' }
    ];

    const encriptarGOST = (texto: string, clave: string = MASTER_KEY): string => {
        try {
            const key = CryptoJS.SHA256(clave);
            const iv = CryptoJS.lib.WordArray.random(16);
            
            const encrypted = CryptoJS.AES.encrypt(texto, key, {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
            
            // Combinamos IV + datos encriptados (similar a GOST-CBC)
            const combined = iv.concat(encrypted.ciphertext);
            return combined.toString(CryptoJS.enc.Base64);
        } catch (error) {
            console.error('Error en encriptación:', error);
            return texto; // Fallback
        }
    };

    const desencriptarGOST = (textoEncriptado: string, clave: string = MASTER_KEY): string => {
        try {
            const key = CryptoJS.SHA256(clave);
            const combined = CryptoJS.enc.Base64.parse(textoEncriptado);
            
            // Extraer IV (primeros 16 bytes) y datos encriptados
            const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));
            const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(4));
            
            const decrypted = CryptoJS.AES.decrypt(
                CryptoJS.lib.CipherParams.create({ ciphertext: encrypted }),
                key,
                {
                    iv: iv,
                    mode: CryptoJS.mode.CBC,
                    padding: CryptoJS.pad.Pkcs7
                }
            );
            
            return decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            console.error('Error en desencriptación:', error);
            return textoEncriptado; // Fallback
        }
    };

    // 🔒 FUNCIÓN PARA ENCRIPTAR DATOS SENSIBLES
    const encriptarDatosSensibles = (licenciaData: any) => {
        return {
            ...licenciaData,
            clave: encriptarGOST(licenciaData.clave),
            descripcion: licenciaData.descripcion ? encriptarGOST(licenciaData.descripcion) : '',
            observaciones: licenciaData.observaciones ? encriptarGOST(licenciaData.observaciones) : ''
        };
    };

    // 🔓 FUNCIÓN PARA DESENCRIPTAR DATOS (para uso futuro)
    const desencriptarDatosSensibles = (licenciaData: any) => {
        return {
            ...licenciaData,
            clave: desencriptarGOST(licenciaData.clave),
            descripcion: licenciaData.descripcion ? desencriptarGOST(licenciaData.descripcion) : '',
            observaciones: licenciaData.observaciones ? desencriptarGOST(licenciaData.observaciones) : ''
        };
    };

    // 🔧 FUNCIÓN PARA ASEGURAR QUE SIEMPRE SEA UN ARRAY
    const ensureArray = (data: any): any[] => {
        if (Array.isArray(data)) {
            return data;
        }
        if (data && typeof data === 'object' && data.success && Array.isArray(data.data)) {
            return data.data;
        }
        if (data && typeof data === 'object' && data.data && Array.isArray(data.data)) {
            return data.data;
        }
        console.warn('Datos recibidos no son un array:', data);
        return [];
    };

    const filteredData = useMemo(() => {
        // 🔧 ASEGURAR QUE DATA SIEMPRE SEA UN ARRAY
        const dataArray = ensureArray(data);
        let filtered = dataArray;

        if (searchTerm) {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item?.tipo?.toLowerCase().includes(searchTermLower) ||
                item?.cveemp?.toLowerCase().includes(searchTermLower) ||
                item?.empleado?.nomemp?.toLowerCase().includes(searchTermLower) ||
                item?.sucursal?.toLowerCase().includes(searchTermLower) ||
                item?.departamento?.toLowerCase().includes(searchTermLower) ||
                item?.estatus?.toLowerCase().includes(searchTermLower)
                // Nota: No buscamos en clave encriptada por seguridad
            );
        }

        if (filtroEstatus) {
            filtered = filtered.filter(item => item?.estatus === filtroEstatus);
        }

        return filtered;
    }, [data, searchTerm, filtroEstatus]);

    // Funciones específicas para licencias con encriptación
    const registrarLicencia = async (licenciaData: any) => {
        // 🔐 Encriptar datos sensibles antes de enviar
        const datosEncriptados = encriptarDatosSensibles(licenciaData);
        
        const response = await fetch(API_URLS.licencias.registrar, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(datosEncriptados),
        });

        if (!response.ok) {
            throw new Error('Error al registrar licencia');
        }

        return await response.json();
    };

    const actualizarLicencia = async (id: string, licenciaData: any) => {
        // 🔐 Encriptar datos sensibles antes de enviar
        const datosEncriptados = encriptarDatosSensibles(licenciaData);
        
        const response = await fetch(API_URLS.licencias.actualizar(id), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(datosEncriptados),
        });

        if (!response.ok) {
            throw new Error('Error al actualizar licencia');
        }

        return await response.json();
    };

    // Cargar datos iniciales
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    obtenerEmpleadosData(),
                    obtenerSucursalesData(),
                    obtenerDepartamentosData()
                ]);
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar los datos iniciales',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    useEffect(() => {
        if (mostrarTabla) {
            obtenerLicenciasData();
        }
    }, [mostrarTabla]);

    const obtenerLicenciasData = async () => {
        try {
            console.log('🔍 Obteniendo licencias desde:', API_URLS.licencias.obtener);
            const result = await obtenerEntidades(API_URLS.licencias.obtener);
            console.log('📦 Resultado obtenido:', result);
            
            // 🔧 ASEGURAR QUE SIEMPRE SEA UN ARRAY
            const licenciasArray = ensureArray(result);
            console.log('✅ Array procesado:', licenciasArray);
            
            setData(licenciasArray);
            
            if (licenciasArray.length === 0) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Sin datos',
                    detail: 'No se encontraron licencias registradas',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('❌ Error al obtener licencias:', error);
            setData([]); // 🔧 ASEGURAR ARRAY VACÍO EN CASO DE ERROR
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar las licencias',
                life: 3000
            });
        }
    };

    const obtenerEmpleadosData = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.empleados.obtener);
            const empleadosArray = ensureArray(result);
            setEmpleados(empleadosArray);
        } catch (error) {
            console.error('Error al obtener empleados:', error);
            setEmpleados([]);
        }
    };

    const obtenerSucursalesData = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.sucursales.obtener);
            const sucursalesArray = ensureArray(result);
            setSucursales(sucursalesArray);
        } catch (error) {
            console.error('Error al obtener sucursales:', error);
            setSucursales([]);
        }
    };

    const obtenerDepartamentosData = async () => {
        try {
            const result = await obtenerEntidades(API_URLS.departamentos.obtener);
            const departamentosArray = ensureArray(result);
            setDepartamentos(departamentosArray);
        } catch (error) {
            console.error('Error al obtener departamentos:', error);
            setDepartamentos([]);
        }
    };

    const obtenerEstadisticasData = async () => {
        try {
            const response = await fetch(API_URLS.licencias.obtenerEstadisticas);
            if (!response.ok) throw new Error('Error al obtener estadísticas');
            const result = await response.json();
            
            if (result.success) {
                setEstadisticas(result.data);
                setMostrarEstadisticas(true);
            }
        } catch (error) {
            console.error('Error al obtener estadísticas:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al cargar las estadísticas',
                life: 3000
            });
        }
    };

    const handleNuevo = () => {
        limpiarFormulario();
        setMode('new');
    };

    const handleEditar = () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione una licencia para editar',
                life: 3000
            });
            return;
        }
        setMode('edit');
    };

    const handleGuardar = async () => {
        try {
            if (!tipo || !clave || !cveemp || !sucursal || !departamento) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Campos requeridos',
                    detail: 'Debe completar todos los campos obligatorios',
                    life: 3000
                });
                return;
            }

            const licenciaData = {
                tipo,
                clave,
                descripcion,
                cveemp,
                sucursal,
                departamento,
                estatus,
                version,
                observaciones
            };

            if (mode === 'new') {
                await registrarLicencia(licenciaData);
                toast.current?.show({
                    severity: 'success',
                    summary: '🔐 ¡Éxito!',
                    detail: 'Licencia registrada y encriptada exitosamente',
                    life: 3000
                });
            } else if (mode === 'edit' && id) {
                await actualizarLicencia(id.toString(), licenciaData);
                toast.current?.show({
                    severity: 'success',
                    summary: '🔐 ¡Actualizado!',
                    detail: 'Licencia modificada y encriptada exitosamente',
                    life: 3000
                });
            }

            limpiarFormulario();
            setMode('view');
            if (mostrarTabla) {
                obtenerLicenciasData();
            }
        } catch (error) {
            console.error('Error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo guardar la licencia',
                life: 3000
            });
        }
    };

    const handleEliminar = async () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione una licencia para eliminar',
                life: 3000
            });
            return;
        }

        try {
            const confirmacion = window.confirm('⚠️ ¿Estás seguro de que deseas eliminar esta licencia?\n\nEsta acción no se puede deshacer.');
            if (!confirmacion) return;

            await eliminarEntidad(API_URLS.licencias.eliminar(id.toString()));

            toast.current?.show({
                severity: 'success',
                summary: '¡Eliminado!',
                detail: 'Licencia eliminada exitosamente',
                life: 3000
            });

            limpiarFormulario();
            setMode('view');
            if (mostrarTabla) {
                obtenerLicenciasData();
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo eliminar la licencia',
                life: 3000
            });
        }
    };

    const handleCambiarEstatus = async () => {
        if (!id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Advertencia',
                detail: 'Seleccione una licencia para cambiar estatus',
                life: 3000
            });
            return;
        }

        try {
            const response = await fetch(API_URLS.licencias.cambiarEstatus(id.toString()), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ estatus })
            });

            if (!response.ok) throw new Error('Error al cambiar estatus');

            toast.current?.show({
                severity: 'success',
                summary: '¡Actualizado!',
                detail: 'Estatus cambiado exitosamente',
                life: 3000
            });
            
            if (mostrarTabla) {
                obtenerLicenciasData();
            }
        } catch (error) {
            console.error('Error al cambiar estatus:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo cambiar el estatus',
                life: 3000
            });
        }
    };

    const handleCancelar = () => {
        limpiarFormulario();
        setMode('view');
    };

    const handleConsultar = () => {
        setMostrarTabla(true);
    };

    const limpiarFormulario = () => {
        setId(null);
        setTipo('');
        setClave('');
        setDescripcion('');
        setCveemp('');
        setSucursal('');
        setDepartamento('');
        setEstatus('ACTIVA');
        setVersion('');
        setObservaciones('');
    };

    const cerrarTabla = () => {
        setMostrarTabla(false);
        setSearchTerm('');
        setFiltroEstatus('');
    };

    const cerrarEstadisticas = () => {
        setMostrarEstadisticas(false);
    };

    const handleSeleccionarLicencia = (licencia: Licencia) => {
        setId(licencia.id);
        setTipo(licencia.tipo);
        // 🔐 Mantenemos la clave encriptada por seguridad
        setClave(licencia.clave);
        setDescripcion(licencia.descripcion || '');
        setCveemp(licencia.cveemp);
        setSucursal(licencia.sucursal);
        setDepartamento(licencia.departamento);
        setEstatus(licencia.estatus);
        setVersion(licencia.version || '');
        setObservaciones(licencia.observaciones || '');
        setMostrarTabla(false);
        setMode('view');
        setSearchTerm('');
        setFiltroEstatus('');
    };

    const handleEmpleadoChange = (empleadoCve: string) => {
        setCveemp(empleadoCve);
        const empleado = empleados.find(emp => emp.cveemp === empleadoCve);
        if (empleado) {
            if (empleado.sucursal) {
                setSucursal(empleado.sucursal);
            }
            if (empleado.departamento) {
                setDepartamento(empleado.departamento);
            }
        }
    };

    // 🔐 FUNCIÓN PARA ALTERNAR VISTA ENCRIPTADA/DESENCRIPTADA (para uso futuro)
    const toggleVistaEncriptacion = () => {
        setMostrarEncriptado(!mostrarEncriptado);
    };

    const getModeIndicator = () => {
        switch (mode) {
            case 'new':
                return { text: 'NUEVA LICENCIA 🔐', color: '#10b981', icon: 'pi-plus' };
            case 'edit':
                return { text: 'EDITANDO LICENCIA 🔐', color: '#f59e0b', icon: 'pi-pencil' };
            default:
                return { text: 'MODO CONSULTA 🔐', color: '#6b7280', icon: 'pi-eye' };
        }
    };

    const modeInfo = getModeIndicator();

    const getEstatusColor = (estatus: string) => {
        switch (estatus) {
            case 'ACTIVA': return '#10b981';
            case 'INACTIVA': return '#6b7280';
            case 'VENCIDA': return '#ef4444';
            case 'REVOCADA': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    return (
        <>
            <Navbar />
            <Sidebar />
            <Toast ref={toast} />

            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>

                {/* Modal de estadísticas */}
                {mostrarEstadisticas && estadisticas && (
                    <div className="overlay" onClick={cerrarEstadisticas} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                    📊 Estadísticas de Licencias 🔐
                                </h3>
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-button-rounded"
                                    onClick={cerrarEstadisticas}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0369a1' }}>{estadisticas.total}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Total</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#16a34a' }}>{estadisticas.por_estatus.activas}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Activas</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#fef2f2', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626' }}>{estadisticas.por_estatus.vencidas}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Vencidas</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6b7280' }}>{estadisticas.por_estatus.inactivas}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Inactivas</div>
                                </div>
                            </div>

                            <h4 style={{ marginBottom: '16px', color: '#1f2937' }}>Licencias por Tipo</h4>
                            <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                                {estadisticas.por_tipo.map((item: any, index: number) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white',
                                        borderRadius: '4px',
                                        marginBottom: '4px'
                                    }}>
                                        <span>{item.tipo}</span>
                                        <span style={{ fontWeight: 'bold', color: '#3b82f6' }}>{item.cantidad}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de la tabla */}
                {mostrarTabla && (
                    <div className="overlay" onClick={cerrarTabla} style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div className="floating-window" onClick={(e) => e.stopPropagation()} style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '1200px',
                            width: '95%',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
                                <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>
                                    🔑 Consultar Licencias 🔐 (Datos Encriptados)
                                </h3>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <div style={{
                                        padding: '4px 8px',
                                        backgroundColor: '#fef3c7',
                                        color: '#92400e',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        fontWeight: '600'
                                    }}>
                                        
                                    </div>
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-text p-button-rounded"
                                        onClick={cerrarTabla}
                                    />
                                </div>
                            </div>

                            {/* Filtros */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px', flexShrink: 0 }}>
                                <div style={{ position: 'relative' }}>
                                    <i className="pi pi-search" style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#9ca3af'
                                    }}></i>
                                    <InputText
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            paddingLeft: '40px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px'
                                        }}
                                        placeholder="Buscar por tipo, empleado, sucursal... (claves encriptadas)"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <Dropdown
                                    value={filtroEstatus}
                                    options={[{ label: 'Todos los estatus', value: '' }, ...estatusOptions]}
                                    onChange={(e) => setFiltroEstatus(e.value)}
                                    placeholder="Filtrar por estatus"
                                    style={{ height: '40px' }}
                                />
                            </div>

                            {/* Contador */}
                            <div style={{ 
                                marginBottom: '15px', 
                                fontSize: '0.9em', 
                                color: '#6b7280',
                                padding: '8px 12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                flexShrink: 0
                            }}>
                                📊 Mostrando <strong>{filteredData.length}</strong> de <strong>{Array.isArray(data) ? data.length : 0}</strong> licencias encriptadas
                            </div>

                            {/* Tabla */}
                            <div style={{ 
                                border: '1px solid #e5e7eb', 
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Header de tabla */}
                                <div style={{ 
                                    backgroundColor: '#f8fafc',
                                    borderBottom: '2px solid #e5e7eb',
                                    flexShrink: 0
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '15%' }}>Tipo</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '25%' }}>🔐 Clave Encriptada</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '15%' }}>Empleado</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '15%' }}>Sucursal</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '15%' }}>Departamento</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '10%' }}>Estatus</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '5%' }}>🔐</th>
                                            </tr>
                                        </thead>
                                    </table>
                                </div>
                                
                                {/* Cuerpo de tabla */}
                                <div style={{ 
                                    flex: 1,
                                    overflowY: 'auto',
                                    maxHeight: 'calc(85vh - 250px)'
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <tbody>
                                            {filteredData.length > 0 ? (
                                                filteredData.map((item, index) => (
                                                    <tr 
                                                        key={item.id} 
                                                        onClick={() => handleSeleccionarLicencia(item)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
                                                    >
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '15%' }}>
                                                            <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>{item.tipo || '-'}</div>
                                                        </td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '25%' }}>
                                                            <div style={{ 
                                                                fontSize: '0.75rem', 
                                                                fontFamily: 'monospace', 
                                                                backgroundColor: '#fef3c7', 
                                                                padding: '4px 8px', 
                                                                borderRadius: '4px', 
                                                                display: 'inline-block',
                                                                border: '1px solid #f59e0b',
                                                                color: '#92400e',
                                                                maxWidth: '200px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                🔐 {item.clave ? item.clave.substring(0, 20) + '...' : 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '15%' }}>
                                                            <div style={{ fontSize: '0.9rem' }}>{item.empleado?.nomemp || item.cveemp || '-'}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.cveemp || '-'}</div>
                                                        </td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '15%', fontSize: '0.9rem' }}>{item.sucursal || '-'}</td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '15%', fontSize: '0.9rem' }}>{item.departamento || '-'}</td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '10%', textAlign: 'center' }}>
                                                            <span style={{
                                                                padding: '4px 8px',
                                                                borderRadius: '12px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: '600',
                                                                color: 'white',
                                                                backgroundColor: getEstatusColor(item.estatus)
                                                            }}>
                                                                {item.estatus || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #f3f4f6', width: '5%', textAlign: 'center' }}>
                                                            <i className="pi pi-lock" style={{ color: '#f59e0b', fontSize: '1rem' }}></i>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={7} style={{ 
                                                        padding: '40px', 
                                                        textAlign: 'center', 
                                                        color: '#6b7280',
                                                        fontSize: '1.1rem'
                                                    }}>
                                                        {Array.isArray(data) && data.length === 0 ? 
                                                            '📝 No hay licencias registradas' : 
                                                            '🔍 No se encontraron licencias que coincidan con los filtros'
                                                        }
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Formulario principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    maxWidth: '1000px',
                    margin: '0 auto',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Header */}
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.8rem', fontWeight: '700' }}>
                                🔑 Gestión de Licencias 🔐
                            </h2>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{
                                    padding: '4px 8px',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                }}>
                                    
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px',
                                    backgroundColor: modeInfo.color,
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}>
                                    <i className={`pi ${modeInfo.icon}`}></i>
                                    {modeInfo.text}
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                    </div>

                    {/* Formulario */}
                    <div style={{ marginBottom: '24px' }}>
                        <form autoComplete="off">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                                {/* Tipo */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        💻 Tipo de Licencia <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={tipo}
                                        options={tiposLicencia}
                                        onChange={(e) => setTipo(e.value)}
                                        placeholder="Seleccione el tipo"
                                        disabled={mode === 'view'}
                                        style={{ width: '100%', height: '40px' }}
                                        editable
                                    />
                                </div>

                                {/* Clave - Ahora con indicador de encriptación */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🔐 Clave/Serial (Se encriptará automáticamente) <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <InputText
                                            value={clave}
                                            onChange={(e) => setClave(e.target.value)}
                                            disabled={mode === 'view'}
                                            placeholder={mode === 'view' ? "Datos encriptados" : "Ingrese la clave o serial"}
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontFamily: mode === 'view' ? 'monospace' : 'inherit',
                                                fontSize: mode === 'view' ? '0.8rem' : '0.9rem',
                                                backgroundColor: mode === 'view' ? '#fef3c7' : 'white',
                                                color: mode === 'view' ? '#92400e' : 'inherit',
                                                paddingRight: '40px'
                                            }}
                                        />
                                        {mode === 'view' && (
                                            <i className="pi pi-lock" style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: '#f59e0b'
                                            }}></i>
                                        )}
                                    </div>
                                </div>

                                {/* Empleado */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        👤 Empleado <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={cveemp}
                                        options={empleados.map(emp => ({ label: `${emp.nomemp} (${emp.cveemp})`, value: emp.cveemp }))}
                                        onChange={(e) => handleEmpleadoChange(e.value)}
                                        placeholder="Seleccione el empleado"
                                        disabled={mode === 'view' || loading}
                                        style={{ width: '100%', height: '40px' }}
                                        filter
                                        loading={loading}
                                    />
                                </div>

                                {/* Sucursal */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🏢 Sucursal <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={sucursal}
                                        options={sucursales.map(suc => ({ label: suc.nombre, value: suc.nombre }))}
                                        onChange={(e) => setSucursal(e.value)}
                                        placeholder="Seleccione la sucursal"
                                        disabled={mode === 'view' || loading}
                                        style={{ width: '100%', height: '40px' }}
                                        filter
                                        loading={loading}
                                    />
                                </div>

                                {/* Departamento */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🏬 Departamento <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <Dropdown
                                        value={departamento}
                                        options={departamentos.map(dep => ({ label: dep.nombre, value: dep.nombre }))}
                                        onChange={(e) => setDepartamento(e.value)}
                                        placeholder="Seleccione el departamento"
                                        disabled={mode === 'view' || loading}
                                        style={{ width: '100%', height: '40px' }}
                                        filter
                                        loading={loading}
                                    />
                                </div>

                                {/* Estatus */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📊 Estatus
                                    </label>
                                    <Dropdown
                                        value={estatus}
                                        options={estatusOptions}
                                        onChange={(e) => setEstatus(e.value)}
                                        disabled={mode === 'view'}
                                        style={{ width: '100%', height: '40px' }}
                                    />
                                </div>

                                {/* Versión */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        🔢 Versión
                                    </label>
                                    <InputText
                                        value={version}
                                        onChange={(e) => setVersion(e.target.value)}
                                        disabled={mode === 'view'}
                                        placeholder="Ej: 2023, 22H2, etc."
                                        style={{ width: '100%', height: '40px' }}
                                    />
                                </div>

                                {/* Descripción - Con indicador de encriptación */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📝 Descripción (Se encriptará automáticamente)
                                    </label>
                                    <InputTextarea
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        disabled={mode === 'view'}
                                        placeholder={mode === 'view' ? "Descripción encriptada" : "Descripción detallada de la licencia"}
                                        rows={2}
                                        style={{ 
                                            width: '100%', 
                                            resize: 'vertical',
                                            backgroundColor: mode === 'view' && descripcion ? '#fef3c7' : 'white',
                                            color: mode === 'view' && descripcion ? '#92400e' : 'inherit',
                                            fontFamily: mode === 'view' && descripcion ? 'monospace' : 'inherit',
                                            fontSize: mode === 'view' && descripcion ? '0.8rem' : 'inherit'
                                        }}
                                    />
                                </div>

                                {/* Observaciones - Con indicador de encriptación */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        📋 Observaciones (Se encriptarán automáticamente)
                                    </label>
                                    <InputTextarea
                                        value={observaciones}
                                        onChange={(e) => setObservaciones(e.target.value)}
                                        disabled={mode === 'view'}
                                        rows={2}
                                        style={{ 
                                            width: '100%', 
                                            resize: 'vertical',
                                            backgroundColor: mode === 'view' && observaciones ? '#fef3c7' : 'white',
                                            color: mode === 'view' && observaciones ? '#92400e' : 'inherit',
                                            fontFamily: mode === 'view' && observaciones ? 'monospace' : 'inherit',
                                            fontSize: mode === 'view' && observaciones ? '0.8rem' : 'inherit'
                                        }}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Botones */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button
                                    icon="pi pi-search"
                                    label="Consultar 🔐"
                                    className="p-button-info p-button-raised"
                                    onClick={handleConsultar}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '140px' }}
                                />
                                <Button
                                    icon="pi pi-chart-bar"
                                    label="Estadísticas"
                                    className="p-button-help p-button-raised"
                                    onClick={obtenerEstadisticasData}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
                                <Button
                                    icon="pi pi-plus"
                                    label="Nuevo 🔐"
                                    className="p-button-success p-button-raised"
                                    onClick={handleNuevo}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
    
                                <Button
                                    icon="pi pi-trash"
                                    label="Eliminar"
                                    className="p-button-danger p-button-raised"
                                    onClick={handleEliminar}
                                    disabled={!id}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px', opacity: !id ? 0.5 : 1 }}
                                />
                            </>
                        ) : (
                            <>
                                <Button
                                    icon="pi pi-check"
                                    label="Guardar 🔐"
                                    className="p-button-success p-button-raised"
                                    onClick={handleGuardar}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '140px' }}
                                />
                                <Button
                                    icon="pi pi-times"
                                    label="Cancelar"
                                    className="p-button-secondary p-button-raised"
                                    onClick={handleCancelar}
                                    style={{ height: '44px', fontSize: '0.95rem', fontWeight: '600', borderRadius: '8px', minWidth: '120px' }}
                                />
                            </>
                        )}
                    </div>

                    {/* Información de seguridad */}
                    <div style={{
                        marginTop: '20px',
                        padding: '12px',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        color: '#92400e'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <i className="pi pi-shield" style={{ fontSize: '1rem' }}></i>
                            <strong>Seguridad Activada</strong>
                        </div>
                        
                    </div>
                </div>
            </div>
        </>
    );
};

export default Licencias;